import type { Candle, CandleInterval } from '../types';

type Regime = 'long' | 'short' | 'neutral';

interface MurreyLevels {
  eightEight: number;
  fourEight: number;
  zeroEight: number;
}

export interface TraderProV3OverlayResult {
  entry?: number;
  tp1?: number;
  tp2?: number;
  stop?: number;
  resistance: number;
  midpoint: number;
  support: number;
  regime: Regime;
  info: string;
}

interface TraderProV3Params {
  candles: Candle[];
  dailyCandles: Candle[];
  symbol: string;
  interval: CandleInterval;
  stopLossRatio?: number;
}

const DEFAULT_STOP_LOSS_RATIO = 1.7;

function toNewestFirst(candles: Candle[]) {
  return [...candles].reverse();
}

function sma(values: number[], length: number): number | undefined {
  if (values.length < length || length <= 0) {
    return undefined;
  }
  let sum = 0;
  for (let i = 0; i < length; i += 1) {
    sum += values[i];
  }
  return sum / length;
}

function uniqueSorted(values: number[]): number[] {
  const rounded = values
    .filter((value) => Number.isFinite(value))
    .map((value) => Math.round(value * 1_000_000) / 1_000_000);
  const unique = Array.from(new Set(rounded));
  unique.sort((a, b) => a - b);
  return unique;
}

function pickClosestBelow(values: number[], target: number): number | undefined {
  let selected: number | undefined;
  for (const value of values) {
    if (value <= target && (selected === undefined || value > selected)) {
      selected = value;
    }
  }
  return selected;
}

function pickClosestAbove(values: number[], target: number): number | undefined {
  let selected: number | undefined;
  for (const value of values) {
    if (value >= target && (selected === undefined || value < selected)) {
      selected = value;
    }
  }
  return selected;
}

function lowest(values: number[], lookback: number): number {
  let low = Number.POSITIVE_INFINITY;
  for (let i = 0; i <= lookback && i < values.length; i += 1) {
    if (values[i] < low) {
      low = values[i];
    }
  }
  return low;
}

function highest(values: number[], lookback: number): number {
  let high = Number.NEGATIVE_INFINITY;
  for (let i = 0; i <= lookback && i < values.length; i += 1) {
    if (values[i] > high) {
      high = values[i];
    }
  }
  return high;
}

function murreyLookback(highs: number[], lows: number[], lookback: number): MurreyLevels {
  const logTen = Math.log(10);
  const log8 = Math.log(8);
  const log2 = Math.log(2);

  const vLow = lowest(lows, lookback);
  const vHigh = highest(highs, lookback);
  const vDist = vHigh - vLow;
  const tmpHigh = vLow < 0 ? 0 - vLow : vHigh;
  const tmpLow = vLow < 0 ? 0 - vLow - vDist : vLow;
  const shifted = vLow < 0;

  const safeTmpHigh = Math.max(tmpHigh, 1e-9);
  const sfVarRaw = Math.log(0.4 * safeTmpHigh) / logTen;
  const sfVar = sfVarRaw - Math.floor(sfVarRaw);

  const sr =
    safeTmpHigh > 25
      ? sfVar > 0
        ? Math.exp(logTen * (Math.floor(Math.log(0.4 * safeTmpHigh) / logTen) + 1))
        : Math.exp(logTen * Math.floor(Math.log(0.4 * safeTmpHigh) / logTen))
      : 100 * Math.exp(log8 * Math.floor(Math.log(0.005 * safeTmpHigh) / log8));

  const range = Math.max(safeTmpHigh - tmpLow, 1e-9);
  const nVar1 = Math.log(sr / range) / log8;
  const nVar2 = nVar1 - Math.floor(nVar1);
  const n = nVar1 <= 0 ? 0 : nVar2 === 0 ? Math.floor(nVar1) : Math.floor(nVar1) + 1;

  const si = sr * Math.exp(-n * log8);
  const m = Math.floor((1 / log2) * Math.log(range / si) + 0.0000001);
  const i = Math.round(((safeTmpHigh + tmpLow) * 0.5) / (si * Math.exp((m - 1) * log2)));

  const bot = (i - 1) * si * Math.exp((m - 1) * log2);
  const top = (i + 1) * si * Math.exp((m - 1) * log2);

  const doShift = safeTmpHigh - top > 0.25 * (top - bot) || bot - tmpLow > 0.25 * (top - bot);
  const mm = !doShift ? m : doShift && m < 2 ? m + 1 : 0;
  const nn = !doShift ? n : doShift && m < 2 ? n : n - 1;

  const finalSi = doShift ? sr * Math.exp(-nn * log8) : si;
  const finalI = doShift
    ? Math.round(((safeTmpHigh + tmpLow) * 0.5) / (finalSi * Math.exp((mm - 1) * log2)))
    : i;
  const finalBot = doShift ? (finalI - 1) * finalSi * Math.exp((mm - 1) * log2) : bot;
  const finalTop = doShift ? (finalI + 1) * finalSi * Math.exp((mm - 1) * log2) : top;

  const increment = (finalTop - finalBot) / 8;
  const absTop = shifted ? -(finalBot - 3 * increment) : finalTop + 3 * increment;

  return {
    eightEight: absTop - 3 * increment,
    fourEight: absTop - 7 * increment,
    zeroEight: absTop - 11 * increment,
  };
}

function computeRegime(_interval: CandleInterval, dailyCandles: Candle[]): Regime {
  const dailyNewest = toNewestFirst(dailyCandles);
  const closeDaily = dailyNewest.map((candle) => candle.close);
  const sma200 = sma(closeDaily, 200);
  const sma21 = sma(closeDaily, 21);
  if (sma21 !== undefined && sma200 !== undefined) {
    if (sma21 > sma200) {
      return 'long';
    }
    if (sma21 < sma200) {
      return 'short';
    }
  }

  return 'neutral';
}

export function computeTraderProV3Overlay({
  candles,
  dailyCandles,
  symbol,
  interval,
  stopLossRatio = DEFAULT_STOP_LOSS_RATIO,
}: TraderProV3Params): TraderProV3OverlayResult | null {
  if (candles.length < 130 || dailyCandles.length < 21) {
    return null;
  }

  const newest = toNewestFirst(candles);
  const highs = newest.map((candle) => candle.high);
  const lows = newest.map((candle) => candle.low);
  const close = newest[0].close;

  const lkbk0 = murreyLookback(highs, lows, 128);
  const lkbk1 = murreyLookback(highs, lows, 32);
  const lkbk2 = murreyLookback(highs, lows, 16);

  const levels = uniqueSorted([
    lkbk0.zeroEight,
    lkbk0.fourEight,
    lkbk0.eightEight,
    lkbk1.zeroEight,
    lkbk1.fourEight,
    lkbk1.eightEight,
    lkbk2.zeroEight,
    lkbk2.fourEight,
    lkbk2.eightEight,
  ]);

  const regime = computeRegime(interval, dailyCandles);
  const symbolLabel = symbol.includes('/') ? symbol : symbol.replace(/([A-Z]{3})$/, '/$1');

  if (regime === 'neutral') {
    return {
      resistance: lkbk0.eightEight,
      midpoint: lkbk0.fourEight,
      support: lkbk0.zeroEight,
      regime,
      info: `Trader Pro v3 ${symbolLabel}: neutral regime`,
    };
  }

  if (regime === 'long') {
    const entry = pickClosestBelow(levels, close) ?? lkbk0.zeroEight;
    const tp1 = pickClosestAbove(levels, entry + Number.EPSILON) ?? lkbk0.fourEight;
    const tp2 = pickClosestAbove(levels, tp1 + Number.EPSILON) ?? lkbk0.eightEight;
    const stop = entry - (tp2 - entry) / stopLossRatio;

    return {
      entry,
      tp1,
      tp2,
      stop,
      resistance: lkbk0.eightEight,
      midpoint: lkbk0.fourEight,
      support: lkbk0.zeroEight,
      regime,
      info: `Trader Pro v3 ${symbolLabel}: long regime`,
    };
  }

  const entry = pickClosestAbove(levels, close) ?? lkbk0.eightEight;
  const tp1Candidates = levels.filter((value) => value < entry).sort((a, b) => b - a);
  const tp1 = tp1Candidates[0] ?? lkbk0.fourEight;
  const tp2 = tp1Candidates[1] ?? lkbk0.zeroEight;
  const stop = entry + (entry - tp2) / stopLossRatio;

  return {
    entry,
    tp1,
    tp2,
    stop,
    resistance: lkbk0.eightEight,
    midpoint: lkbk0.fourEight,
    support: lkbk0.zeroEight,
    regime,
    info: `Trader Pro v3 ${symbolLabel}: short regime`,
  };
}
